import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';
import { 
  ChevronRight, ChevronLeft, Check, AlertCircle,
  User, FileText, CreditCard, Shield, PenTool
} from 'lucide-react';
import { inscriptionsAPI, tarifsAPI, settingsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const STEPS = [
  { id: 'infos', title: 'Vos informations', icon: User },
  { id: 'tarifs', title: 'Choix du Cours', icon: CreditCard },
  { id: 'autorisations', title: 'Autorisations', icon: Shield },
  { id: 'cgv', title: 'CGV & Signature', icon: PenTool },
];

const InscriptionAdultePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const sigCanvas = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [tarifs, setTarifs] = useState([]);
  
  const [formData, setFormData] = useState({
    // Informations personnelles
    civilite: '',
    nom: '',
    prenom: '',
    date_naissance: '',
    nationalite: 'Française',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    profession: '',
    // Niveau et objectifs
    niveau_actuel: '',
    objectifs: '',
    disponibilites: '',
    // Cours choisi
    cours_choisi: null,
    // CPF
    utilise_cpf: false,
    numero_cpf: '',
    // Autorisations
    autorisation_photo: true,
    newsletter: true,
    cgv_acceptees: false,
    // Signature
    lieu_signature: '',
    date_signature: new Date().toLocaleDateString('fr-FR'),
    signature_image: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tarifsRes, settingsRes] = await Promise.all([
        tarifsAPI.getAll(),
        settingsAPI.get()
      ]);
      // Filtrer les tarifs adultes
      const adulteTarifs = tarifsRes.data.filter(t => 
        t.groupe.toLowerCase().includes('adulte') || 
        t.groupe.toLowerCase().includes('conversation') ||
        t.groupe.toLowerCase().includes('business') ||
        t.age_range.toLowerCase().includes('adulte') ||
        t.age_range.toLowerCase().includes('18+') ||
        t.age_range.toLowerCase().includes('professionnel')
      );
      setTarifs(adulteTarifs);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
    updateFormData('signature_image', '');
  };

  const saveSignature = () => {
    if (sigCanvas.current?.isEmpty()) {
      toast({ title: 'Erreur', description: 'Veuillez signer le document', variant: 'destructive' });
      return;
    }
    const dataUrl = sigCanvas.current.toDataURL('image/png');
    updateFormData('signature_image', dataUrl);
    toast({ title: 'Signature enregistrée' });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Infos personnelles
        return formData.nom && formData.prenom && formData.email && formData.telephone && formData.adresse;
      case 1: // Tarifs
        return formData.cours_choisi !== null;
      case 2: // Autorisations
        return true;
      case 3: // CGV
        return formData.cgv_acceptees && formData.signature_image && formData.lieu_signature;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs obligatoires', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Transformer les données pour l'API
      const inscriptionData = {
        type: 'adulte',
        nombre_enfants: 0,
        enfants: [],
        adresse: `${formData.adresse}, ${formData.code_postal} ${formData.ville}`,
        pere: null,
        mere: null,
        // Informations adulte
        adulte: {
          civilite: formData.civilite,
          nom: formData.nom,
          prenom: formData.prenom,
          date_naissance: formData.date_naissance,
          nationalite: formData.nationalite,
          telephone: formData.telephone,
          email: formData.email,
          profession: formData.profession,
          niveau_actuel: formData.niveau_actuel,
          objectifs: formData.objectifs,
          disponibilites: formData.disponibilites,
          utilise_cpf: formData.utilise_cpf,
          numero_cpf: formData.numero_cpf,
        },
        cours_choisis: formData.cours_choisi ? [{
          groupe: formData.cours_choisi.groupe,
          horaire: formData.cours_choisi.horaire,
          tarif: formData.cours_choisi.prix_1er,
          numero_enfant: 1
        }] : [],
        documents_ids: [],
        allergies: '',
        personnes_autorisees: [],
        autorisation_photo: formData.autorisation_photo,
        cgv_acceptees: formData.cgv_acceptees,
        lieu_signature: formData.lieu_signature,
        date_signature: formData.date_signature,
        signature_image: formData.signature_image,
      };

      await inscriptionsAPI.create(inscriptionData);
      navigate('/inscription/confirmation');
    } catch (error) {
      console.error('Error submitting:', error);
      toast({ 
        title: 'Erreur', 
        description: 'Une erreur est survenue lors de l\'envoi du formulaire', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-[#2C3C65]">Vos informations personnelles</h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Civilité</label>
                <select
                  value={formData.civilite}
                  onChange={(e) => updateFormData('civilite', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#285BBB]"
                >
                  <option value="">Sélectionner</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nom *</label>
                <Input
                  value={formData.nom}
                  onChange={(e) => updateFormData('nom', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Prénom *</label>
                <Input
                  value={formData.prenom}
                  onChange={(e) => updateFormData('prenom', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Date de naissance</label>
                <Input
                  type="date"
                  value={formData.date_naissance}
                  onChange={(e) => updateFormData('date_naissance', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nationalité</label>
                <Input
                  value={formData.nationalite}
                  onChange={(e) => updateFormData('nationalite', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Adresse *</label>
              <Input
                value={formData.adresse}
                onChange={(e) => updateFormData('adresse', e.target.value)}
                placeholder="Numéro et rue"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Code postal</label>
                <Input
                  value={formData.code_postal}
                  onChange={(e) => updateFormData('code_postal', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Ville</label>
                <Input
                  value={formData.ville}
                  onChange={(e) => updateFormData('ville', e.target.value)}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Téléphone *</label>
                <Input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => updateFormData('telephone', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Profession</label>
              <Input
                value={formData.profession}
                onChange={(e) => updateFormData('profession', e.target.value)}
              />
            </div>

            <hr className="my-6" />

            <h3 className="text-xl font-semibold text-[#2C3C65]">Votre niveau et objectifs</h3>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Niveau actuel en anglais</label>
              <select
                value={formData.niveau_actuel}
                onChange={(e) => updateFormData('niveau_actuel', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#285BBB]"
              >
                <option value="">Sélectionner</option>
                <option value="debutant">Débutant (A1)</option>
                <option value="elementaire">Élémentaire (A2)</option>
                <option value="intermediaire">Intermédiaire (B1)</option>
                <option value="intermediaire_plus">Intermédiaire avancé (B2)</option>
                <option value="avance">Avancé (C1)</option>
                <option value="expert">Expert (C2)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Vos objectifs</label>
              <Textarea
                value={formData.objectifs}
                onChange={(e) => updateFormData('objectifs', e.target.value)}
                placeholder="Ex: Améliorer mon anglais professionnel, préparer le TOEIC..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Vos disponibilités</label>
              <Textarea
                value={formData.disponibilites}
                onChange={(e) => updateFormData('disponibilites', e.target.value)}
                placeholder="Ex: Mardi et jeudi soir après 18h..."
                rows={2}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-[#2C3C65]">Choisissez votre formation</h3>
            
            {/* Option CPF */}
            <div className="bg-blue-50 rounded-xl p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.utilise_cpf}
                  onChange={(e) => updateFormData('utilise_cpf', e.target.checked)}
                  className="mt-1 h-5 w-5 text-[#285BBB] rounded"
                />
                <div>
                  <span className="font-semibold text-[#2C3C65]">Je souhaite utiliser mon CPF</span>
                  <p className="text-sm text-[#6777A1] mt-1">
                    Nos formations sont éligibles au Compte Personnel de Formation
                  </p>
                </div>
              </label>
              
              {formData.utilise_cpf && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Numéro CPF (optionnel)</label>
                  <Input
                    value={formData.numero_cpf}
                    onChange={(e) => updateFormData('numero_cpf', e.target.value)}
                    placeholder="Votre numéro de dossier CPF"
                  />
                </div>
              )}
            </div>

            {/* Liste des tarifs */}
            <div className="space-y-4">
              {tarifs.length > 0 ? tarifs.map((tarif) => (
                <div
                  key={tarif.id}
                  onClick={() => updateFormData('cours_choisi', tarif)}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    formData.cours_choisi?.id === tarif.id
                      ? 'border-[#285BBB] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[#2C3C65]">{tarif.groupe}</h4>
                      <p className="text-sm text-[#6777A1]">{tarif.niveau} • {tarif.horaire}</p>
                      <p className="text-sm text-[#6777A1]">{tarif.jour}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#285BBB]">{tarif.prix_1er}€</p>
                      <p className="text-xs text-[#6777A1]">par an</p>
                      {tarif.is_cpf && (
                        <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                          Éligible CPF
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-[#6777A1]">
                  Aucune formation adulte disponible pour le moment.
                </div>
              )}
            </div>

            {/* Frais d'inscription */}
            {formData.cours_choisi && (
              <div className="bg-gray-50 rounded-xl p-4 mt-6">
                <div className="flex justify-between items-center">
                  <span className="text-[#6777A1]">Formation choisie</span>
                  <span className="font-semibold">{formData.cours_choisi.prix_1er}€</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-[#6777A1]">Frais d'inscription</span>
                  <span className="font-semibold">{settings.frais_inscription || 25}€</span>
                </div>
                <hr className="my-3" />
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[#2C3C65]">Total</span>
                  <span className="text-xl font-bold text-[#285BBB]">
                    {formData.cours_choisi.prix_1er + (settings.frais_inscription || 25)}€
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-[#2C3C65]">Autorisations</h3>

            <div className="space-y-4">
              <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.autorisation_photo}
                  onChange={(e) => updateFormData('autorisation_photo', e.target.checked)}
                  className="mt-1 h-5 w-5 text-[#285BBB] rounded"
                />
                <div>
                  <span className="font-medium text-[#2C3C65]">Droit à l'image</span>
                  <p className="text-sm text-[#6777A1] mt-1">
                    J'autorise The English Speaking Center à utiliser mon image (photos, vidéos) 
                    prises lors des cours ou événements à des fins de communication.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.newsletter}
                  onChange={(e) => updateFormData('newsletter', e.target.checked)}
                  className="mt-1 h-5 w-5 text-[#285BBB] rounded"
                />
                <div>
                  <span className="font-medium text-[#2C3C65]">Newsletter</span>
                  <p className="text-sm text-[#6777A1] mt-1">
                    J'accepte de recevoir les actualités et offres de The English Speaking Center par email.
                  </p>
                </div>
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-[#2C3C65]">Conditions Générales de Vente</h3>

            <div className="bg-gray-50 rounded-lg p-4 h-48 overflow-y-auto text-sm text-[#6777A1]">
              <h4 className="font-semibold text-[#2C3C65] mb-2">Article 1 - Objet</h4>
              <p className="mb-4">
                Les présentes CGV régissent les relations contractuelles entre The English Speaking Center 
                et ses apprenants dans le cadre des formations en anglais.
              </p>
              <h4 className="font-semibold text-[#2C3C65] mb-2">Article 2 - Inscription</h4>
              <p className="mb-4">
                L'inscription est définitive après réception du dossier complet et du règlement. 
                Les frais d'inscription de {settings.frais_inscription || 25}€ ne sont pas remboursables.
              </p>
              <h4 className="font-semibold text-[#2C3C65] mb-2">Article 3 - Paiement</h4>
              <p className="mb-4">
                Le paiement peut être effectué en une ou plusieurs fois. En cas de financement CPF, 
                les modalités sont définies par la plateforme Mon Compte Formation.
              </p>
              <h4 className="font-semibold text-[#2C3C65] mb-2">Article 4 - Annulation</h4>
              <p>
                Toute annulation doit être notifiée par écrit. Un remboursement partiel peut être 
                accordé selon les conditions définies dans le règlement intérieur.
              </p>
            </div>

            <label className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={formData.cgv_acceptees}
                onChange={(e) => updateFormData('cgv_acceptees', e.target.checked)}
                className="mt-1 h-5 w-5 text-[#285BBB] rounded"
                required
              />
              <div>
                <span className="font-medium text-[#2C3C65]">J'accepte les CGV *</span>
                <p className="text-sm text-[#6777A1] mt-1">
                  Je reconnais avoir lu et accepté les Conditions Générales de Vente.
                </p>
              </div>
            </label>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Fait à *</label>
              <Input
                value={formData.lieu_signature}
                onChange={(e) => updateFormData('lieu_signature', e.target.value)}
                placeholder="Ville"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Le</label>
              <Input
                value={formData.date_signature}
                disabled
                className="bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Signature *</label>
              <div className="border-2 border-gray-300 rounded-lg bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'w-full h-40',
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={clearSignature}>
                  Effacer
                </Button>
                <Button size="sm" onClick={saveSignature}>
                  Valider la signature
                </Button>
              </div>
              {formData.signature_image && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  Signature enregistrée
                </p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    index <= currentStep 
                      ? 'bg-[#285BBB] text-white' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index < currentStep ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <step.icon className="h-5 w-5" />
                    )}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`hidden sm:block w-16 md:w-24 h-1 mx-2 ${
                      index < currentStep ? 'bg-[#285BBB]' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2">
              {STEPS.map((step, index) => (
                <span 
                  key={step.id}
                  className={`text-xs md:text-sm ${
                    index <= currentStep ? 'text-[#285BBB] font-medium' : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </span>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8"
          >
            {renderStep()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Précédent
              </Button>

              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  className="bg-[#285BBB] hover:bg-[#1e4a9a]"
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Envoi en cours...' : 'Valider l\'inscription'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InscriptionAdultePage;
